// Persons per function — ONE place for the rule, used by Headcount, Cards and
// Expenses (a food estimate can take a function's headcount).
//
//   named guests   parties deduped by mobile (a family invited by several
//                  members counts once); each party = its ExpectedCount.
//   extra cards    a family head may ask for extra cards for people whose names
//                  are unknown. They are invited only to the "extra-card guests
//                  come to" functions of the card type marked ForExtraCards
//                  (the Reception), and count ExtraPersons, or
//                  EXTRA_PERSONS_PER_CARD per card.
import { EXTRA_PERSONS_PER_CARD } from './helpers';

export const rowFunctionIds = (row) => {
  if (row.FunctionIds) return String(row.FunctionIds).split(',').map(v => v.trim()).filter(Boolean);
  return row.FunctionId ? [String(row.FunctionId)] : [];
};

// One household = one card: the family (FamilyId), else the mobile, else the guest.
export const householdKey = (r) => (Number(r.FamilyId || 0)
  ? `fam:${r.FamilyId}` : (r.MobileNumber ? `mob:${r.MobileNumber}` : `g:${r.GuestId}`));

export const extraPersonsFor = (extraCards, extraPersons) => {
  const cards = Math.max(0, Number(extraCards || 0));
  if (!cards) return 0;
  const typed = Number(extraPersons || 0);
  return typed > 0 ? typed : cards * EXTRA_PERSONS_PER_CARD;
};

// The functions extra-card guests come to: the extra-card type's
// ExtraFunctionIds (normally just the Reception), else all its FunctionIds.
export const extraCardFunctionIds = (cardTypes) => new Set(
  (cardTypes || []).filter(t => Number(t.ForExtraCards) === 1)
    .flatMap(t => String(t.ExtraFunctionIds || t.FunctionIds || '').split(',').map(v => v.trim()).filter(Boolean))
);

// Extra cards per household (every row of a household carries the same value).
export function householdExtras(rows) {
  const m = new Map();
  (rows || []).forEach(r => {
    const k = householdKey(r);
    const cards = Number(r.ExtraCards || 0);
    const prev = m.get(k);
    if (!prev || cards > prev.extraCards) m.set(k, { extraCards: cards, extraPersons: Number(r.ExtraPersons || 0), side: r.Side || 'Common' });
  });
  return m;
}

const sideOf = (s) => (s === 'Male' ? 'Groom' : s === 'Female' ? 'Bride' : (s || 'Common'));

// → { parties, persons, named, extra, extraCards, bySide } for one function.
export function functionHeadcount(rows, functionId, cardTypes) {
  const id = String(functionId);
  const seen = new Map();
  (rows || []).filter(r => rowFunctionIds(r).includes(id)).forEach(r => {
    const key = r.MobileNumber || `g${r.GuestId}`;
    const size = Number(r.ExpectedCount || 1);
    const prev = seen.get(key);
    if (!prev || size > prev.size) seen.set(key, { size, side: r.Side || 'Common' });
  });
  const bySide = { Groom: 0, Bride: 0, Common: 0 };
  let named = 0;
  seen.forEach(v => { named += v.size; const s = sideOf(v.side); bySide[s] = (bySide[s] || 0) + v.size; });

  let extra = 0;
  let extraCards = 0;
  if (extraCardFunctionIds(cardTypes).has(id)) {
    householdExtras(rows).forEach(h => {
      const p = extraPersonsFor(h.extraCards, h.extraPersons);
      if (!p) return;
      extra += p; extraCards += h.extraCards;
      const s = sideOf(h.side); bySide[s] = (bySide[s] || 0) + p;
    });
  }
  return { parties: seen.size, named, extra, extraCards, persons: named + extra, bySide };
}

// How many invitation CARDS one function needs - and with them, whatever is
// handed over with a card (a gift at the Thread Ceremony, a sweet box ...).
// Food is counted per person; a card is counted per HOUSEHOLD, so the two
// numbers an order is placed on are never the same number.
//   households  one per family / mobile invited to this function, the hosts'
//               own family and count-only groups left out (needsCard)
//   extraCards  the copies heads asked for, on the functions those guests come
//               to (the ForExtraCards type's ExtraFunctionIds - the Reception)
//   cards       households + extraCards = what to order for this function
export function functionCards(rows, functionId, cardTypes, needsCard) {
  const id = String(functionId);
  const seen = new Set();
  (rows || []).filter(r => (needsCard ? needsCard(r) : true))
    .filter(r => rowFunctionIds(r).includes(id))
    .forEach(r => seen.add(householdKey(r)));
  let extraCards = 0;
  if (extraCardFunctionIds(cardTypes).has(id)) {
    householdExtras((rows || []).filter(r => (needsCard ? needsCard(r) : true)))
      .forEach(h => { extraCards += Math.max(0, Number(h.extraCards || 0)); });
  }
  return { households: seen.size, extraCards, cards: seen.size + extraCards };
}

// Cards for the WHOLE event - the number the printer (and the gift with each
// card) is ordered on: every household invited to any OPEN function and given
// a card at all, plus the extra copies heads asked for. Same as the Brief
// Report's "Cards to print".
export function eventCards(rows, openFunctionIds, needsCard) {
  const open = new Set([...(openFunctionIds || [])].map(String));
  const extras = new Map();
  (rows || []).filter(r => (needsCard ? needsCard(r) : true))
    .filter(r => rowFunctionIds(r).some(id => open.has(String(id))))
    .forEach(r => {
      const k = householdKey(r);
      extras.set(k, Math.max(extras.get(k) || 0, Number(r.ExtraCards || 0)));
    });
  const extraCards = [...extras.values()].reduce((n, v) => n + v, 0);
  return { households: extras.size, extraCards, cards: extras.size + extraCards };
}

// ── Distribution: how many cards a person is carrying, gave, still owes ────
// One place for the three numbers the Cards screen is read for, because they
// are read together and must be in the SAME unit: physical cards, extra copies
// included. A head who asked for 3 extra cards is 3 more to carry, so
// `copies` - not `cards`, which counts cards to WRITE (one per household).
// `cards` is the list produced by useCards (one entry per household, carrying
// `extraCards` and `status`).
export const cardTally = (list) => list.reduce((t, c) => {
  const n = 1 + Math.max(0, Number(c.extraCards || 0));
  t.cards += 1; t.copies += n;
  if (c.status === 'Delivered') { t.given += 1; t.givenCopies += n; } else t.pending += 1;
  return t;
}, { cards: 0, copies: 0, given: 0, givenCopies: 0, pending: 0 });

// Grouped by the MEMBER WHO INVITED the household (`addedById` = CreatedBy) -
// on this event the member who invited a household is the one who gives them
// their card, so this is the share each person is asked for. Counted over every
// card of the event, never a filtered view: "Sanjay has 40 cards, 12 given"
// must mean the same number whatever is on screen. `me` is always a row even
// before they have invited anybody, because the filter has to show that.
//
// ONE CARD PER HOUSEHOLD (owner's decision, 2026-10-29). total / given /
// pending count HOUSEHOLDS - the extra copies a head asked for are shown beside
// their name and counted only where cards are ORDERED (`copies`, kept here for
// that), never added into what somebody has to hand over. Mixing the two gave
// the same person 3/3 in one place and 15/15 in another.
// The bucket for cards whose `CreatedBy` was never saved - a real row on the
// live event, and one the screen must be able to show.
export const NO_ADDER = '—';

export function cardsByAdder(list, { meId, myName } = {}) {
  const mine = String(meId == null ? '' : meId);
  const byUser = new Map();
  // A card whose adder was never recorded still has to be given by SOMEBODY.
  // Dropping those rows (what this did until 2026-10-31) meant the members'
  // totals quietly added up to less than the event's - the live screen read
  // "Everyone's cards 4/316" while no member's list could reach 316, and those
  // cards could not be opened from any filter. They now get a row of their
  // own, last in the list, so the numbers reconcile and the cards are reachable.
  list.forEach(c => {
    const id = String(c.addedById || '') || NO_ADDER;
    if (!byUser.has(id)) {
      byUser.set(id, {
        value: id,
        name: id === NO_ADDER ? 'Nobody recorded' : (c.addedBy || `User ${id}`),
        list: [],
      });
    }
    const row = byUser.get(id);
    if (id !== NO_ADDER && !row.name && c.addedBy) row.name = c.addedBy;
    row.list.push(c);
  });
  if (mine && !byUser.has(mine)) byUser.set(mine, { value: mine, name: myName || 'You', list: [] });
  return [...byUser.values()]
    .map(u => {
      const t = cardTally(u.list);
      return {
        value: u.value,
        name: u.name,
        label: `${u.name}${u.value === mine ? ' (you)' : ''}`,
        noAdder: u.value === NO_ADDER,
        total: t.cards,             // households = cards to give
        given: t.given,             // households handed over
        pending: t.pending,
        households: t.cards,
        copies: t.copies,           // + the extra copies - for ORDERING only
        givenCopies: t.givenCopies,
        extraCards: t.copies - t.cards,
        families: u.list.filter(c => Number(c.persons || 1) > 1).length,
      };
    })
    // Me first, the cards nobody is recorded against last, the rest by size.
    .sort((a, b) => (b.value === mine) - (a.value === mine)
      || (a.value === NO_ADDER) - (b.value === NO_ADDER)
      || b.total - a.total || a.name.localeCompare(b.name));
}
